import React from "react";
import ActionLogger from "action-logger";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";

import MembershipHelpers, { isActiveMembership } from "helpers/membership-helpers";
import MembershipItemsHelpers from "helpers/membership-items-helpers";
import { isRental as isRentalOld } from "helpers/price-helper";
import { isRental } from "helpers/product/merchandising-category-helper";
import AtomLinkButton from "components/source/atoms/atom-link-button";
import { selectFeatureFlagEnabled } from "selectors/featureFlagSelectors";
import { featureFlags, inventoryEligibilities } from "rtr-constants";
import { navigateTo } from "helpers/location-helpers";
import { membershipStatePropType, userDataPropType } from "components/propTypes";
import {
  withOnLoadFlagsAndExperiments,
  flagsAndExperimentNames,
  flagsAndExperimentsPropType,
} from "components/source/hoc/with-flags-and-experiments";

export const handleReserveCtaClick = (handleWayfindCtaClick, product, pdpDrawerUrl) => {
  ActionLogger.logAction({
    object_type: "node",
    action: "click_rent_now_reserve",
    styleId: product.id || product.productId,
  });

  if (pdpDrawerUrl) {
    const pdpUrl = new URL(pdpDrawerUrl, window.location);
    pdpUrl.searchParams.append("lens", "classic");
    const pdpFullPath = `${pdpUrl.pathname}${pdpUrl.search}`;
    navigateTo(pdpFullPath);
  } else {
    handleWayfindCtaClick();
  }
};
export class MembershipPickWayfind extends React.Component {
  static propTypes = {
    basePath: PropTypes.string,
    flagsAndExperiments: flagsAndExperimentsPropType,
    gMMembershipPricingDiscount40FF: PropTypes.bool,
    pdpDrawerUrl: PropTypes.string,
    handleWayfindCtaClick: PropTypes.func,
    navigateToPdp: PropTypes.func,
    isBuyNow: PropTypes.bool,
    product: PropTypes.shape({
      id: PropTypes.string,
      price: PropTypes.shape({
        isBulk: PropTypes.bool,
        isClearance: PropTypes.bool,
        isRental: PropTypes.bool,
      }),
      merchandisingCategory: PropTypes.string,
    }),
    userData: userDataPropType,
    membershipState: membershipStatePropType,
    manuallyShowOption: PropTypes.oneOf(["reserve", "membership"]),
  };

  isMembershipLens() {
    return MembershipHelpers.isSubscriptionLens(this.props.userData);
  }

  getLensAndAvailability() {
    const { membershipState, product, userData } = this.props;

    if (this.isMembershipLens() || MembershipHelpers.isNoIntentLens(userData)) {
      return { lens: "classic", available: MembershipItemsHelpers.isAvailableForReserve(product) };
    }

    if (MembershipHelpers.hasInventoryEligibility(membershipState, inventoryEligibilities.rtr_unlimited)) {
      return {
        lens: "unlimited",
        available: MembershipItemsHelpers.isAvailableForUnlimited(product),
      };
    }

    if (MembershipHelpers.hasInventoryEligibility(membershipState, inventoryEligibilities.rtr_update)) {
      return {
        lens: "rtrupdate",
        available: MembershipItemsHelpers.isAvailableForUpdate(product),
      };
    }

    return { lens: null, available: false };
  }

  findIntendedLens() {
    return this.getLensAndAvailability().lens;
  }

  isProductAvailableForWayfind() {
    return this.getLensAndAvailability().available;
  }

  // At this time this is used every year as a seasonal membership sale 5/02/2022
  membershipDiscount() {
    return this.props.gMMembershipPricingDiscount40FF ? "40" : "25";
  }

  shouldRender() {
    const { product } = this.props;

    const inTabsExperiment =
      !this.props.isBuyNow && this.props.flagsAndExperiments?.[flagsAndExperimentNames.RSV_1494_ATB_TABS];

    return (
      (isRental(product) || isRentalOld(product?.price)) && this.isProductAvailableForWayfind() && !inTabsExperiment
    );
  }

  render() {
    if (!this.shouldRender()) return null;

    const { membershipState } = this.props;
    const { handleWayfindCtaClick, navigateToPdp, product, pdpDrawerUrl, basePath } = this.props;
    const isMembershipLens = this.isMembershipLens();
    const baseClasses = "flex items-baseline flex-wrap p-0";

    const clickHandler = () => {
      if (navigateToPdp) {
        if (!basePath) return;
        navigateTo(`${basePath}?lens=${this.findIntendedLens()}`);
      } else {
        handleWayfindCtaClick();
      }
    };

    const switchToReserveRent = (
      <div className={baseClasses}>
        <AtomLinkButton
          className="pr-1 font-bold"
          onClick={() => handleReserveCtaClick(clickHandler, product, pdpDrawerUrl)}>
          Reserve in Advance
        </AtomLinkButton>
        {isActiveMembership(membershipState) && (
          <span className="text-darkGray"> (members get {this.membershipDiscount()}% off)</span>
        )}
      </div>
    );
    const switchToMembershipRent = (
      <div className={`${baseClasses} mt-6 mb-1`} data-test-id="switch-to-reserve-link">
        Rent this with your membership.{" "}
        <AtomLinkButton className="pl-1 pr-0 font-bold" onClick={clickHandler}>
          Check Availability
        </AtomLinkButton>
      </div>
    );

    if (this.props.manuallyShowOption === "reserve") return switchToReserveRent;
    if (this.props.manuallyShowOption === "membership") return switchToMembershipRent;

    return isMembershipLens ? switchToReserveRent : switchToMembershipRent;
  }
}

const mapStateToProps = state => ({
  gMMembershipPricingDiscount40FF: selectFeatureFlagEnabled(featureFlags.GM_MEMBERSHIP_PRICING_DISCOUNT_40)(state),
  pdpDrawerUrl: state.gridProductDrawer?.pdpUrl,
  basePath: state.basePath,
  isBuyNow: state.isBuyNow,
});

export default compose(
  connect(mapStateToProps),
  withOnLoadFlagsAndExperiments(flagsAndExperimentNames.RSV_1494_ATB_TABS)
)(MembershipPickWayfind);
